import { Box, SxProps, Table, TableProps, useTheme } from "@mui/material"
import { PropsWithChildren, useEffect, useRef, useState } from "react"
import { useAppSelector } from "../../../redux/hooks";
import { commonState } from "../../../redux/common/commonSlice";

interface RPMTableScrollableProps {
    id?: string,
    widthExtra?: number,
    containerSxProps?: SxProps,
    tableProps?: TableProps,
    autoScrollRightTriggerDeps?: React.DependencyList
}

export const RPMTableScrollable = ({
    children, id, widthExtra, containerSxProps, tableProps, autoScrollRightTriggerDeps
}: PropsWithChildren<RPMTableScrollableProps>) => {

    const common = useAppSelector(commonState);
    const theme = useTheme();
    const [hasWindowScrollVertical, setHasWindowScrollVertical] = useState(false);

    const calculateWidthReduction = (hasWindowScroll: boolean) => {
        return (common.menuExpanded ? 240 : 65) +
            (common.listExpanded ? 300 : 26) +
            (hasWindowScroll ? 14 : 0) +  // vertical scroll bar takes up space
            (document.body.clientWidth - document.body.scrollWidth) +  // If horizontal scroll - then change the size of the grid to the available space
            (widthExtra ?? 70);
    }

    const tableRef = useRef(null);
    const [widthReduction, setWidthReduction] =
        useState(calculateWidthReduction(hasWindowScrollVertical));

    const checkForScrollbar = () => {
        // Check if the window has a vertical scroll
        const hasWindowScroll = document.body.offsetHeight > globalThis.innerHeight;
        setHasWindowScrollVertical(hasWindowScroll);
        // Set deduction width of the table
        setWidthReduction(calculateWidthReduction(hasWindowScroll));
    }

    useEffect(() => {
        // Set the width in certain conditions
        // eslint-disable-next-line        
        setWidthReduction(calculateWidthReduction(hasWindowScrollVertical));
        // we want only the conditions below
        // eslint-disable-next-line
    }, [common.menuExpanded, common.listExpanded])

    useEffect(() => {
        if (autoScrollRightTriggerDeps && tableRef) {
            // reference can be any
            // eslint-disable-next-line
            const tableCurrent: any = tableRef.current;
            if (tableCurrent?.scrollTo) {
                tableCurrent.scrollTo({ left: tableCurrent.scrollWidth });
            }
        }
    }, [autoScrollRightTriggerDeps])

    useEffect(() => {
        // check if we need to add a scroll bar
        // eslint-disable-next-line        
        checkForScrollbar();
        // we want only the conditions below
        // eslint-disable-next-line
    }, [tableRef, children]);


    // Add a onresize function to check for a scrollbar
    // eslint-disable-next-line        
    globalThis.onresize = function () {
        checkForScrollbar();
    }

    const size = tableProps ? tableProps.size : undefined;

    return (
        <Box
            sx={{ width: `calc(100vw - ${widthReduction + 20}px)` }}
        >
            <Box
                id={`${id}-container`}
                ref={tableRef}
                sx={{
                    width: `calc(100vw - ${widthReduction}px)`,
                    overflow: 'auto',
                    border: `1px solid ${theme.palette.grey[300]}`,
                    borderRadius: '4px',
                    transition: 'width 300ms ease-out',
                    ...containerSxProps
                }}
            >
                <Table id={id} size={size ?? "small"} {...tableProps}>
                    {children}
                </Table>
            </Box>
        </Box>
    )
}
